import {ClassificationTier, ClassificationTierTarget} from '../../api-client-kt/src';
import {sortBy} from 'es-toolkit';
import {Stdlib} from '../lib/stdlib';

export interface ClassificationTierTargetWithName extends ClassificationTierTarget {
    tierName: string;
}

export const DefaultTieringRanges = [
    { fromValue: 0.0, toValue: 0.9 },
    { fromValue: 0.9, toValue: 1.0 },
    { fromValue: 1.0, toValue: 1.2 },
    { fromValue: 1.2, toValue: 1.3 }
];

/**
 * This data class represents a scheme for classifying portfolio segments into distinct tiers.
 * Provides operations for managing and retrieving tier information and their associated targets.
 */
export class PortfolioSegmentClassificationScheme {

    // A constant that represents a default empty classification scheme with no tiers or targets,
    // which can be used as a fallback or default value.
    static EMPTY = new PortfolioSegmentClassificationScheme([], []);

    // tiers sorted from low to high
    readonly tiers: ClassificationTier[];

    // tier names sorted by lowest tier first
    readonly tierNames: string[];

    // tier targets aligned in order and count of tiers
    readonly tierTargets: ClassificationTierTargetWithName[];

    constructor(tiers: ClassificationTier[], tierTargets: ClassificationTierTarget[]) {
        // initialization makes sure that the tier names are in the correct order
        const tiersFromLowToHigh = sortBy(tiers, ['position']);
        const tiersCount = tiersFromLowToHigh.length;
        this.tiers = tiersFromLowToHigh;
        this.tierNames = tiersFromLowToHigh.map(tier => tier.name!);

        // tier target search function with exception when tier not found
        // which would indicate a wrong configuration of the segment classification scheme
        function tierTargetByTierId(tier: ClassificationTier) {
            const index = tiersFromLowToHigh.indexOf(tier);
            const indexReverse = tiersCount - index - 1;
            const target = tierTargets.find(tt => tt.classificationTierId === tier.id);
            const retval = target ?? {
                classificationTierId: tier.id,
                    classificationTierKey: tier.key,
                    fromValue: DefaultTieringRanges[index].fromValue,
                    toValue: (index < tiersCount - 1) ? DefaultTieringRanges[index].toValue : undefined,
                    targetValue: index,
                    targetRateChangeMin: -0.05,
                    targetRateChangeMax: (indexReverse + 1) * 0.05,
                    targetRateChangeAdjustment: 0,
                    targetRateChange: indexReverse/10,
                    targetRetentionRate: 1.0 - indexReverse * 0.1,
                    targetRateChangeRemaining: indexReverse/10,
                    targetRetentionRateRemaining: 1.0 - indexReverse * 0.1 - 0.025
            };
            if (retval.targetRateChangeAdjustment == undefined) target!.targetRateChangeAdjustment = 0;
            return retval;
        }

        this.tierTargets = tiersFromLowToHigh.map((tier) => (
            {...tierTargetByTierId(tier), tierName: tier.name!}
        ));
    }

    private getTierTarget(tierName: string): ClassificationTierTargetWithName {
        const target = this.tierTargets.find(t => t.tierName === tierName);
        if (!target) throw new Error(`No tiering range data found for tier ${tierName}`);
        return target;
    }

    tierData(tierName: string): ClassificationTierTarget {
        const tierTarget = this.getTierTarget(tierName);
        if (!tierTarget) throw new Error(`No tiering range data found for tier ${tierName}`);
        return tierTarget;
    }

    tiersNamesHighestFirst(): string[] {
        return this.tierNames.slice().reverse();
    }

    updateTargetValue(tierName: string, attribute: string, value: number | undefined | null) {
        const tierTarget = this.getTierTarget(tierName);
        tierTarget[attribute as keyof ClassificationTierTarget] = value as never;
    }
}
